import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {PrettyPrinter} from "../../../components/Utils/PrettyPrinter";
import {MarketingTabMapper} from "../utils/MarketingTabMapper";
import {BarChartRow} from "./BarChart";
import "./Chart.scss";
import {veboxDefaultTheme} from "../../../index";

interface BarChartDataEntity {
    name: string;
    value: number;
    value2: number;
    color: string;
}

interface BarChartProps {
    width: number;
    height: number;
    color: string;
    data: BarChartDataEntity[];
    yUnitOfMeasure: string;
    y1UnitOfMeasure: string;
}

interface BarChartState {
    selectedItem: string;
}

export class BarChart extends React.Component<BarChartProps, BarChartState> {

    private static DEFAULT_COLOR: string = "var(--vebox-gray-hover-light)";
    /** The color of histogram bars. */
    private static BAR_COLOR: string = "#75ca7e";

    private randomElementsPrefix: string;

    constructor(props: BarChartProps) {
        super(props);
        this.state = {selectedItem: null};
        this.setSelectedItem = this.setSelectedItem.bind(this);
        this.randomElementsPrefix = Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, "")
            .substr(0, 10);
    }

    public setSelectedItem(itemName: string): void {
        this.setState({selectedItem: itemName});
    }

    public render() {
        // Create the element
        const div = new ReactFauxDOM.Element("div");
        const data = this.props.data;
        let color = BarChart.BAR_COLOR;
        if (this.props.color && this.props.color != "") {
            color = this.props.color;
        }

        const margin = {top: 0, right: 30, bottom: 30, left: 40},
            width = this.props.width - margin.left - margin.right,
            height = this.props.height - margin.top - margin.bottom - 80;
        const barSpacePercentage = 0.35;
        const barWidth = (width / data.length) / (1 + barSpacePercentage);
        const barSpace = (width / data.length) * barSpacePercentage;
        // create svg for histogram.
        const gWidth = this.props.width + 30;
        const gHeight = this.props.height;
        const svg = d3.select(div)
            .append("svg")
            .attr("width", gWidth)
            .attr("height", this.props.height)
            .append("g")
            .attr("transform", `translate(${margin.left}, 40)`)
            .attr("width", gWidth)
            .attr("height", gHeight);

        svg.append("text")
            .text(this.props.yUnitOfMeasure)
            .attr("transform", `translate(-40, -30)`)
            .classed("chart__unit-of-measure", true);
        svg.append("text")
            .text(this.props.y1UnitOfMeasure)
            .attr("transform", `translate(${width - 50}, -30)`)
            .classed("chart__unit-of-measure", true);
        // create function for x-axis mapping.
        const x = d3.scaleBand()
            .rangeRound([0, width])
            .domain(data.map(function (d: BarChartDataEntity) {
                    return d.name;
                }),
            );
        // Create function for y-axis map.
        const y = d3.scaleLinear()
            .range([height, 0])
            .domain(
                [
                    0,
                    d3.max(data, function (d: BarChartDataEntity) {
                        return d.value;
                    }),
                ],
            );
        // Create function for y-axis map.
        const y1 = d3.scaleLinear()
            .range([height, 0])
            .domain(
                [
                    0,
                    d3.max(data, function (d: BarChartDataEntity) {
                        return d.value2;
                    }),
                ],
            );
        // Add x-axis to the histogram svg.
        if (data.length > 7) {
            svg.append("g")
                .attr("class", "x axis")
                .attr("transform", `translate(0,${height})`)
                .call((d3 as any).axisBottom().scale(x))
                .selectAll("text")
                .attr("data-id", (d, i) => {
                    return this.randomElementsPrefix + "dDateText" + i;
                })
                .attr("class", "dateFieldsClass")
                .style("font-size", "10px")
                .style("fill", "black")
                .attr("y", 9)
                .attr("x", 3)
                .attr("dy", ".35em")
                .attr("transform", "rotate(35)")
                .style("text-anchor", "start");
        } else {
            svg.append("g")
                .attr("class", "x axis")
                .attr("transform", `translate(0,${height})`)
                .call((d3 as any).axisBottom().scale(x))
                .selectAll("text")
                .attr("data-id", (d, i) => {
                    return this.randomElementsPrefix + "dDateText" + i;
                })
                .attr("class", "dateFieldsClass")
                .style("font-size", "10px")
                .style("fill", "black")
                .style("text-anchor", "middle");
        }
        const customAxis = function (g) {
            g.selectAll("text")
                .attr("x", -30)
                .attr("dy", 4)
                .style("fill", "black");
        };

        const customAxis1 = function (g) {
            g.selectAll("text")
                .attr("x", -30)
                .attr("dy", 4)
                .style("fill", "black");
        };

        const yAxisLeft = (d3 as any).axisRight()
            .ticks(8)
            .scale(y)
            .tickSize(width);

        const yAxisRight = (d3 as any).axisRight()
            .ticks(7)
            .scale(y1);

        svg.append("g")
            .attr("class", "y axis")
            .style("fill", "grey")
            .call(yAxisLeft)
            .call(customAxis);

        svg.append("g")
            .attr("class", "y axis y1")
            // .style("fill", "grey")
            .call(yAxisRight)
            .call(customAxis1)
            .attr("transform", `translate(${width + 45}, 0)`);

        svg.selectAll("g.y.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.x.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.y.axis").selectAll("line").style("display", "").style("stroke", "#c0c0c0");
        svg.selectAll("g.x.axis").selectAll("line").style("display", "").style("stroke", "black");
        svg.selectAll("g.y.axis.y1").selectAll("line").style("display", "none");
        y.domain([0, d3.max(data, function (d: BarChartDataEntity) {
            return Math.max(d.value);
        })]);
        y1.domain([0, d3.max(data, function (d: BarChartDataEntity) {
            return Math.max(d.value2);
        })]);

        const line = d3.line()
            .x((d: any) => x(d.name) + (barWidth * 0.5) + barSpace / 2)
            .y((d: any) => y1(d.value2));
        // Create bars for histogram to contain rectangles and freq labels.
        const bars = svg.selectAll(".bar").data(data).enter().append("g").attr("class", "bar");
        // create the rectangles.
        bars.append("rect")
            .attr("x", function (d: BarChartDataEntity) {
                return x(d.name) + barSpace / 2; // + barWidth + 3;
            })
            .attr("y", function (d: BarChartDataEntity) {
                return y(d.value);
            })
            .attr("width", barWidth)
            .attr("data-id", (d: BarChartRow) => {
                return this.randomElementsPrefix + MarketingTabMapper.codifyEntityName(d.name);
            })
            .attr("height", function (d: BarChartDataEntity) {
                return height - y(d.value);
            })
            .attr("fill", function (d: BarChartDataEntity) {
                if (d.color && d.color != "") {
                    return d.color;
                }
                return color;
            })
            .style("opacity", ".8")
            .on("mouseover", (event, d: BarChartDataEntity) => {
                this.setSelectedItem(d.name);
            })
            .on("mouseout", () => {
                this.setSelectedItem(null);
            });
        // Create the frequency labels above the rectangles.
        const displayLabels = bars.size() <= 7;
        bars.append("text")
            .text(function (d: BarChartDataEntity) {
                return PrettyPrinter.salesQuantity(d.value);
            })
            .attr("data-id", this.randomElementsPrefix + "bar-frequency-value")
            .attr("x", function (d: BarChartDataEntity) {
                return x(d.name) + (barWidth * 0.5) + barSpace / 2; // + 3;
            })
            .attr("y", function (d: BarChartDataEntity) {
                return y(d.value) - 5;
            })
            .attr("text-anchor", "middle")
            .classed("bars-text-field-" + this.randomElementsPrefix, true)
            .attr("data-id", (d: BarChartDataEntity) => {
                return "text-field-" + this.randomElementsPrefix + MarketingTabMapper.codifyEntityName(d.name);
            })
            .style("display", displayLabels ? "" : "none");

        svg.append("path")
            .datum(data)
            .attr("fill", "none")
            .attr("stroke", veboxDefaultTheme.palette.brand.main)
            .attr("stroke-linejoin", "round")
            .attr("stroke-linecap", "round")
            .attr("stroke-width", 1.2)
            .attr("d", line as any)
            .style("opacity", ".8");

        const circles = svg.selectAll("circle")
            .data(data)
            .enter()
            .append("circle")
            .on("mouseover", () => {
                d3.selectAll(".bars-text-field-" + this.randomElementsPrefix)
                    .style("display", "none");
                d3.selectAll("[data-id=" + this.randomElementsPrefix + "circle-frequency-value]")
                    .style("display", "");
            })
            .on("mouseout", () => {
                if (bars.size() < 8) {
                    d3.selectAll(".bars-text-field-" + this.randomElementsPrefix)
                        .style("display", "");
                }
                d3.selectAll("[data-id=" + this.randomElementsPrefix + "circle-frequency-value]")
                    .style("display", "none");
            });
        circles.attr("cx", function (d) {
            return x(d.name) + (barWidth * 0.5) + barSpace / 2;
        })
            .attr("cy", function (d) {
                return y1(d.value2);
            })
            .attr("r", 4)
            .style("fill", veboxDefaultTheme.palette.brand.main)
            .style("opacity", ".8");

        // Create the frequency labels above the rectangles.
        bars.append("text")
            .text(function (d: BarChartDataEntity) {
                return d.value2.toFixed(0);
            })
            .attr("data-id", this.randomElementsPrefix + "circle-frequency-value")
            .attr("x", function (d: BarChartDataEntity) {
                return x(d.name) + (barWidth * 0.5) + barSpace / 2; // + 3;
            })
            .attr("y", function (d: BarChartDataEntity) {
                return y1(d.value2) - 5;
            })
            .attr("text-anchor", "middle")
            .classed("circle-text-field", true)
            .style("display", "none");
        if (this.state.selectedItem != null) {
            bars.selectAll("[data-id=" + this.randomElementsPrefix + MarketingTabMapper.codifyEntityName(
                this.state.selectedItem) + "]")
                .style("fill", BarChart.DEFAULT_COLOR);
            bars.selectAll("[data-id=text-field-" + this.randomElementsPrefix + MarketingTabMapper.codifyEntityName(
                this.state.selectedItem) + "]")
                .style("display", "");
        }
        const normalizeText = (text: string): string => {
            if (text.length > 19) {
                text = text.slice(0, 18) + "...";
            }
            return text;
        };
        svg.selectAll("g.x.axis").selectAll("text").each(function (d, i) {
            d3.select(this).text(normalizeText(d3.select(this).text()));
        });
        return div.toReact();
    }
}
